import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import type { TSlidesFolder } from "@/shared/types";
import { slidesFolderApi } from "./slides-folder.api";
import { slidesFolderKeys } from "./slides-folder.keys";

const collectFolderAndDescendants = (
  folders: TSlidesFolder[],
  rootId: string,
): Set<string> => {
  const childrenByParent = new Map<string, string[]>();
  for (const folder of folders) {
    if (!folder.parentId) continue;
    const bucket = childrenByParent.get(folder.parentId);
    if (bucket) bucket.push(folder._id);
    else childrenByParent.set(folder.parentId, [folder._id]);
  }
  const result = new Set<string>([rootId]);
  const queue: string[] = [rootId];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    const children = childrenByParent.get(current);
    if (!children) continue;
    for (const child of children) {
      if (!result.has(child)) {
        result.add(child);
        queue.push(child);
      }
    }
  }
  return result;
};

export const useCreateSlidesFolderMutation = () => {
  return useMutation({
    mutationFn: (data: {
      title: string;
      parentId?: string;
    }) => slidesFolderApi.createSlidesFolder(data),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: slidesFolderKeys.slidesFolders,
      });
      const previousFolders = reactQueryUtils.getQueryData<TSlidesFolder[]>([
        ...slidesFolderKeys.slidesFolders,
      ]);

      return { previousFolders };
    },
    onSuccess: (data, _variables, context) => {
      if (data && context?.previousFolders) {
        const optimisticFolders = [...context.previousFolders, data];
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          optimisticFolders,
        );
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFolders) {
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          context.previousFolders,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...slidesFolderKeys.slidesFolders]);
    },
  });
};

export const useUpdateSlidesFolderMutation = () => {
  return useMutation({
    mutationFn: (data: {
      id: string;
      title?: string;
      parentId?: string | null;
      type?: "team-slides" | "user-slides";
    }) => slidesFolderApi.updateSlidesFolder(data),
    onMutate: async ({ id, title, parentId }) => {
      await queryClient.cancelQueries({
        queryKey: slidesFolderKeys.slidesFolders,
      });
      const previousFolders = reactQueryUtils.getQueryData<TSlidesFolder[]>([
        ...slidesFolderKeys.slidesFolders,
      ]);

      if (previousFolders) {
        const optimisticFolders = previousFolders.map((folder) =>
          folder._id === id
            ? {
                ...folder,
                ...(title !== undefined && { title }),
                ...(parentId !== undefined && { parentId }),
              }
            : folder,
        );
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          optimisticFolders,
        );
      }

      return { previousFolders };
    },
    onSuccess: (data, { id }) => {
      if (data) {
        const previousFolders = reactQueryUtils.getQueryData<TSlidesFolder[]>([
          ...slidesFolderKeys.slidesFolders,
        ]);

        if (previousFolders) {
          const updatedFolders = previousFolders.map((folder) =>
            folder._id === id ? { ...folder, ...data } : folder,
          );
          reactQueryUtils.setQueryData(
            [...slidesFolderKeys.slidesFolders],
            updatedFolders,
          );
        }
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFolders) {
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          context.previousFolders,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...slidesFolderKeys.slidesFolders]);
    },
  });
};

export const useReorderSlidesFoldersMutation = () => {
  return useMutation({
    mutationFn: (data: { parentId: string; folderIds: string[] }) =>
      slidesFolderApi.reorderSlidesFolders(data),
    onMutate: async ({ parentId, folderIds }) => {
      await queryClient.cancelQueries({
        queryKey: slidesFolderKeys.slidesFolders,
      });
      const previousFolders = reactQueryUtils.getQueryData<TSlidesFolder[]>([
        ...slidesFolderKeys.slidesFolders,
      ]);

      if (previousFolders) {
        const orderById = new Map(folderIds.map((id, index) => [id, index]));
        const optimisticFolders = previousFolders.map((folder) => {
          if (folder.parentId !== parentId) return folder;
          const nextOrder = orderById.get(folder._id);
          if (nextOrder === undefined) return folder;
          return { ...folder, order: nextOrder };
        });
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          optimisticFolders,
        );
      }

      return { previousFolders };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFolders) {
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          context.previousFolders,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...slidesFolderKeys.slidesFolders]);
    },
  });
};

export const useArchiveSlidesFolderMutation = () => {
  return useMutation({
    mutationFn: (id: string) => slidesFolderApi.archiveSlidesFolder(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: slidesFolderKeys.slidesFolders,
      });
      const previousFolders = reactQueryUtils.getQueryData<TSlidesFolder[]>([
        ...slidesFolderKeys.slidesFolders,
      ]);

      if (previousFolders) {
        // Server cascades; mirror that locally.
        const idsToRemove = collectFolderAndDescendants(previousFolders, id);
        const optimisticFolders = previousFolders.filter(
          (folder) => !idsToRemove.has(folder._id),
        );
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          optimisticFolders,
        );
      }

      return { previousFolders };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFolders) {
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.slidesFolders],
          context.previousFolders,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...slidesFolderKeys.slidesFolders]);
      reactQueryUtils.invalidateQueries([
        ...slidesFolderKeys.archivedSlidesFolders,
      ]);
    },
  });
};

export const useRestoreSlidesFolderMutation = () => {
  return useMutation({
    mutationFn: (id: string) => slidesFolderApi.restoreSlidesFolder(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: slidesFolderKeys.archivedSlidesFolders,
      });
      const previousArchived = reactQueryUtils.getQueryData<TSlidesFolder[]>([
        ...slidesFolderKeys.archivedSlidesFolders,
      ]);

      if (previousArchived) {
        // Restoring a root also restores descendants; remove them locally.
        const idsToRemove = collectFolderAndDescendants(previousArchived, id);
        const optimisticArchived = previousArchived.filter(
          (folder) => !idsToRemove.has(folder._id),
        );
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.archivedSlidesFolders],
          optimisticArchived,
        );
      }

      return { previousArchived };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousArchived) {
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.archivedSlidesFolders],
          context.previousArchived,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([
        ...slidesFolderKeys.archivedSlidesFolders,
      ]);
      reactQueryUtils.invalidateQueries([...slidesFolderKeys.slidesFolders]);
    },
  });
};

export const useDeleteSlidesFolderMutation = () => {
  return useMutation({
    mutationFn: (id: string) => slidesFolderApi.deleteSlidesFolder(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: slidesFolderKeys.archivedSlidesFolders,
      });
      const previousArchived = reactQueryUtils.getQueryData<TSlidesFolder[]>([
        ...slidesFolderKeys.archivedSlidesFolders,
      ]);

      if (previousArchived) {
        const idsToRemove = collectFolderAndDescendants(previousArchived, id);
        const optimisticArchived = previousArchived.filter(
          (folder) => !idsToRemove.has(folder._id),
        );
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.archivedSlidesFolders],
          optimisticArchived,
        );
      }

      return { previousArchived };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousArchived) {
        reactQueryUtils.setQueryData(
          [...slidesFolderKeys.archivedSlidesFolders],
          context.previousArchived,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([
        ...slidesFolderKeys.archivedSlidesFolders,
      ]);
    },
  });
};
